import { Injectable } from '@angular/core';
import createDebug from 'debug';

const debug = createDebug('BeyondBasics:PageService');

export interface PageMetadata {
  name: string;
  path: string;
  displayName: string;
  description: string;
  sidenavIcon?: string;
  isDefault: boolean;
  roles: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class PageService {
  readonly names = [
    'About',
    'Activity',
    'AlternateStudentAvailability',
    'AlternateStudentNotAvailabileDays',
    'Attendance',
    'Curriculum',
    'Curriculums',
    'Login',
    'NotAuthorized',
    'OrganizationDetails',
    'Organization',
    'OrganizationProperties',
    'Organizations',
    'ResourceCategory',
    'Resource',
    'ResourceSet',
    'ResourceSets',
    'Resources',
    'SelectedStudentNotAvailabileDays',
    'SelectedStudentSchedule',
    'Session',
    'Slots',
    'StudentOnlineSession',
    'Student',
    'SwitchUser',
    'TermAvailability',
    'Term',
    'TestArticle',
    'TestArticles',
    'TestResult',
    'TutorAvailability',
    'TutorOnlineSession',
    'Tutor',
    'TutorSchedule',
    'Tutors',
    'UserActivity',
    'UserRoles',
    'Welcome',
  ] as const;

  readonly metadata: readonly PageMetadata[] = [
    {
      name: 'About',
      path: '/about',
      displayName: 'About',
      description:
        "Shows the app version and the release notes from news/ (newest first). AD2 listed versions via its platform ChangeLogService; that history lived in the AD2 platform, so the AD3 page starts fresh with this repo's hand-authored release notes.",
      sidenavIcon: 'info',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'Activity',
      path: '/activities/:activity.id',
      displayName: 'Activity',
      description:
        'One activity of a curriculum: its items as a drag-to-reorder accordion. Locked versions and non-designers get a read-only form.',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'AlternateStudentAvailability',
      path: '/alternateStudentAvailabilities/:alternateStudent.id',
      displayName: 'Alternate Student Availability',
      description:
        "A weekday-by-period grid of the term's slots: clicking a cell toggles whether the alternate student is available for that slot.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'AlternateStudentNotAvailabileDays',
      path: '/alternateStudentNotAvailabileDays/:alternateStudent.id',
      displayName: 'Alternate Student Not Availabile Days',
      description:
        'Manages an alternate student\'s not-available days. (The AD2 path\'s "Availabile" spelling is preserved so existing links keep working.)',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'Attendance',
      path: '/attendance',
      displayName: 'Attendance',
      description:
        "Daily attendance: pick a date, term, and tutor to record the tutor's attendance table, or leave the tutor blank for the whole-term daily roster (downloadable as CSV). Selecting a term generates the day's attendance for all of its tutors.",
      sidenavIcon: 'how_to_reg',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'Tutor', 'CustomerAccountManager'],
    },
    {
      name: 'Curriculum',
      path: '/curriculums/:curriculum.id',
      displayName: 'Curriculum',
      description:
        'One curriculum version: its fields and ordered activities, with the versioned-content actions (new version, publish, archive, delete) chosen by status. Locked versions and non-designers get a read-only form.',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'Curriculums',
      path: '/curriculums',
      displayName: 'Curriculums',
      description:
        "Lists curriculums (latest version of each by default, all versions on demand) with add and navigation to each curriculum's page.",
      sidenavIcon: 'menu_book',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'Login',
      path: '/login',
      displayName: 'Login',
      description: 'Public login page with a button to initiate OIDC login.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'NotAuthorized',
      path: '/not-authorized',
      displayName: 'Not Authorized',
      description:
        'Displayed when a user authenticates but is not registered in the system.',
      isDefault: false,
      roles: ['Everyone'],
    },
    {
      name: 'OrganizationDetails',
      path: '/organizationDetails/:organization.id',
      displayName: 'Organization Details',
      description:
        "Edits an organization's fields and its property values (text, number, or select-one, defined by the organization properties), with a dialog to manage which properties apply.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'Organization',
      path: '/organizations/:organization.id',
      displayName: 'Organization',
      description:
        "One organization, with side-by-side sections for its terms (newest first) and students (by last then first name), a link to the organization's details page, and an admin-only delete button.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'OrganizationProperties',
      path: '/organizationProperties',
      displayName: 'Organization Properties',
      description:
        'Manages the property definitions that organizations can carry values for, as a drag-to-reorder accordion.',
      sidenavIcon: 'description',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'Organizations',
      path: '/organizations',
      displayName: 'Organizations',
      description:
        "Lists all organizations with search, add, and navigation to each organization's page.",
      sidenavIcon: 'school',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'ResourceCategory',
      path: '/resourceCategories/:resourceCategory.id',
      displayName: 'Resource Category',
      description:
        'One category of a resource set: its name and pass criteria beside the ordered resources, with an autocomplete to add published resources. Locked versions and non-designers get a read-only form.',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'Resource',
      path: '/resources/:resource.id',
      displayName: 'Resource',
      description:
        'One resource version, edited with the editor for its type (fill in the blank, click the word, PDF, move pictures, move letters, or Tattum board), with the versioned-content actions chosen by status. Locked versions and non-designers get a read-only form.',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'ResourceSet',
      path: '/resourceSets/:resourceSet.id',
      displayName: 'Resource Set',
      description:
        'One resource-set version: its name and ordered categories, with the versioned-content actions (new version, publish, archive, delete) chosen by status. Locked versions and non-designers get a read-only form.',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'ResourceSets',
      path: '/resourceSets',
      displayName: 'Resource Sets',
      description:
        "Lists resource sets (latest version of each by default, all versions on demand) with add and navigation to each resource set's page.",
      sidenavIcon: 'category',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'Resources',
      path: '/resources',
      displayName: 'Resources',
      description:
        "Lists resources (latest version of each by default, all versions on demand) with add and navigation to each resource's page.",
      sidenavIcon: 'library_books',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'SelectedStudentNotAvailabileDays',
      path: '/selectedStudentNotAvailabileDays/:selectedStudent.id',
      displayName: 'Selected Student Not Availabile Days',
      description:
        'Manages a selected student\'s not-available days. (The AD2 path\'s "Availabile" spelling is preserved so existing links keep working.)',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'SelectedStudentSchedule',
      path: '/selectedStudentSchedule/:selectedStudent.id',
      displayName: 'Selected Student Schedule',
      description:
        "The student's weekly tutor schedule as of a chosen date, with proposed changes shown dashed (and pending deletions struck through) when enabled.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'Session',
      path: '/sessions/:session.id',
      displayName: 'Session',
      description:
        "A tutoring session's log: the curriculum's activity items as one column per activity (text, number, select-one, and resource pickers grouped by category with linked-resource markers), session notes, and the complete flag. Editable by admins, site leaders, and the session's tutor.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'Tutor', 'CustomerAccountManager'],
    },
    {
      name: 'Slots',
      path: '/terms/:term.id/slots',
      displayName: 'Slots',
      description:
        "Manages a term's weekly times: an accordion to edit each time and its weekday slots beside a calendar view of the resulting schedule.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'StudentOnlineSession',
      path: '/studentOnlineSession/:student.id',
      displayName: 'Student Online Session',
      description:
        "The student's side of a live online session: a welcome message with the session's status, and the shared resource viewer showing whatever the tutor has started. Joins the session's socket room for today's session.",
      isDefault: false,
      roles: ['Administrator', 'Student'],
    },
    {
      name: 'Student',
      path: '/students/:student.id',
      displayName: 'Student',
      description:
        'One student: their fields, test results (newest first, with an add button that opens a fresh test result), and prescriptions (with an autocomplete to prescribe a published curriculum), plus organization change and admin-only delete.',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'SwitchUser',
      path: '/switch-user',
      displayName: 'Switch User',
      description:
        'Allows administrators to switch to another user by selecting from a list. Only available when ALLOW_IMPERSONATION is enabled on the server. Selecting a user sets sessionStorage and reloads the app.',
      isDefault: false,
      roles: [],
    },
    {
      name: 'TermAvailability',
      path: '/terms/:term.id/availability',
      displayName: 'Term Availability',
      description:
        "Manages a term's non-instructional days: the unavailability accordion beside the month calendar.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'Term',
      path: '/terms/:term.id',
      displayName: 'Term',
      description:
        'One term: its fields and manager contacts, with side-by-side sections for selected students, alternate students, and tutors, links to the slots and non-instructional-days pages, and an admin-only delete button.',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'TestArticle',
      path: '/testArticles/:testArticle.id',
      displayName: 'Test Article',
      description:
        'One reading-assessment article: its fields and the attached PDF, with upload/delete and an inline pdfjs viewer.',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'TestArticles',
      path: '/testArticles',
      displayName: 'Test Articles',
      description:
        "Lists all test articles with search, add, and navigation to each test article's page.",
      sidenavIcon: 'article',
      isDefault: false,
      roles: ['Administrator', 'CurriculumDesigner'],
    },
    {
      name: 'TestResult',
      path: '/testResults/:testResult.id',
      displayName: 'Test Result',
      description:
        'One reading assessment. Historical results (recognized by their test article) render the legacy layout: type, sound scores, and the achieved and working articles with derived fluency. New results render the restructured layout (opps 3648/5291): cycle and stage, the 15-level decoding sub-scores in four tiers, and per-side fluency level, words-per-minute, and errors.',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'TutorAvailability',
      path: '/tutorAvailability/:termTutor.id',
      displayName: 'Tutor Availability',
      description: "Manages a term tutor's not-available days.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'TutorOnlineSession',
      path: '/tutorOnlineSession/:session.id',
      displayName: 'Tutor Online Session',
      description:
        "The tutor's side of a live online session: the session log's activity items in a collapsible column (with play/stop on each resource picker), and the shared resource viewer. Starting a resource relays it to the student through the session's socket room.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'Tutor'],
    },
    {
      name: 'Tutor',
      path: '/tutors/:tutor.id',
      displayName: 'Tutor',
      description: 'One tutor: their fields, with an admin-only delete button.',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'TutorSchedule',
      path: '/tutorSchedule/:termTutor.id',
      displayName: 'Tutor Schedule',
      description:
        "The tutor's weekly grid of student and activity assignments as of a chosen date. Site leaders drag students/activities onto slots (or off the grid) to propose changes; customer account managers approve or reject them, singly or all at once.",
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'Tutors',
      path: '/tutors',
      displayName: 'Tutors',
      description:
        "Lists all tutors with a show-inactive toggle, TSV import/export, and navigation to each tutor's page.",
      sidenavIcon: 'person',
      isDefault: false,
      roles: ['Administrator', 'SiteLeader', 'CustomerAccountManager'],
    },
    {
      name: 'UserActivity',
      path: '/user-activity',
      displayName: 'User Activity',
      description:
        'Administrator report of active time by user for a selected range. The range is chosen as a period unit (hour, day, week, month) plus a relative choice (e.g. "This Week" or "Last Week"). Active time is the number of activity periods times the period length.',
      sidenavIcon: 'insights',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'UserRoles',
      path: '/userRoles',
      displayName: 'User Roles',
      description:
        "A user-by-role checkbox matrix: toggling assigns or removes the role (assigning Tutor also creates the tutor record), the action column clears all of a user's roles, and new users can be added by email or imported in bulk as tutors.",
      sidenavIcon: 'people',
      isDefault: false,
      roles: ['Administrator'],
    },
    {
      name: 'Welcome',
      path: '/welcome',
      displayName: 'Welcome',
      description:
        'Routes each signed-in user to their landing page by role (students to their online session, tutors to attendance, curriculum designers to curriculums, staff to organizations). Users with no registered role see a message asking them to contact their site leader.  The AD2 default page, and the AD3 default too now that the starter home page is removed.',
      isDefault: true,
      roles: [],
    },
  ];

  readonly sidenavPages: readonly PageMetadata[] = [
    this.metadata.find((p) => p.name === 'Organizations')!,
    this.metadata.find((p) => p.name === 'Tutors')!,
    this.metadata.find((p) => p.name === 'Attendance')!,
    this.metadata.find((p) => p.name === 'Curriculums')!,
    this.metadata.find((p) => p.name === 'ResourceSets')!,
    this.metadata.find((p) => p.name === 'Resources')!,
    this.metadata.find((p) => p.name === 'TestArticles')!,
    this.metadata.find((p) => p.name === 'UserRoles')!,
    this.metadata.find((p) => p.name === 'OrganizationProperties')!,
    this.metadata.find((p) => p.name === 'UserActivity')!,
    this.metadata.find((p) => p.name === 'About')!,
  ];

  getMetadata(name: string): PageMetadata | undefined {
    return this.metadata.find((m) => m.name === name);
  }
}
